import { supabase } from "./supabase";
import homePage from "@/data/pages/home";
import aboutPage from "@/data/pages/about";
import howItWorksPage from "@/data/pages/how-it-works";
import orderPage from "@/data/pages/order";
import { collectionsData } from "@/data/collections";

/* ── Pages ────────────────────────────────────────────── */

const defaults = {
  home: homePage,
  about: aboutPage,
  "how-it-works": howItWorksPage,
  order: orderPage,
};

export const pagesMeta = [
  { slug: "home", name: "Accueil", route: "/" },
  { slug: "about", name: "À propos", route: "/a-propos" },
  { slug: "how-it-works", name: "Comment ça marche", route: "/comment-ca-marche" },
  { slug: "order", name: "Commander", route: "/commander" },
];

export function getDefaultPage(slug) {
  return defaults[slug] ? structuredClone(defaults[slug]) : null;
}

export async function getPageDraft(slug) {
  const { data, error } = await supabase
    .from("page_content")
    .select("draft_content, published_content, has_draft, updated_at")
    .eq("page_slug", slug)
    .maybeSingle();

  if (error) throw error;
  if (!data) return null;

  return {
    content: data.has_draft ? data.draft_content : data.published_content,
    hasDraft: data.has_draft,
    updatedAt: data.updated_at,
  };
}

export async function savePageDraft(slug, content) {
  const { error } = await supabase.from("page_content").upsert(
    {
      page_slug: slug,
      draft_content: content,
      has_draft: true,
      updated_at: new Date().toISOString(),
    },
    { onConflict: "page_slug" }
  );
  if (error) throw error;
}

export async function getAllDraftStatuses() {
  const { data, error } = await supabase
    .from("page_content")
    .select("page_slug, has_draft, updated_at");
  if (error) return { statuses: {}, tableReady: false };
  const statuses = {};
  data?.forEach((r) => {
    statuses[r.page_slug] = { hasDraft: r.has_draft, updatedAt: r.updated_at };
  });
  return { statuses, tableReady: true };
}

/* ── Collections ──────────────────────────────────────── */

export function getDefaultCollections() {
  return Object.values(collectionsData).map((col) => structuredClone(col));
}

export function getDefaultCollection(slug) {
  return collectionsData[slug] ? structuredClone(collectionsData[slug]) : null;
}

export async function getAllCollectionDrafts() {
  const { data, error } = await supabase
    .from("collections")
    .select("slug, draft_data, published_data, has_draft, sort_order, updated_at")
    .order("sort_order", { ascending: true });

  if (error) return { collections: null, tableReady: false };

  const collections = data.map((row) => ({
    slug: row.slug,
    data: row.has_draft ? row.draft_data : row.published_data,
    hasDraft: row.has_draft,
    updatedAt: row.updated_at,
    sortOrder: row.sort_order,
  }));
  return { collections, tableReady: true };
}

export async function getCollectionDraft(slug) {
  const { data, error } = await supabase
    .from("collections")
    .select("draft_data, published_data, has_draft, updated_at")
    .eq("slug", slug)
    .maybeSingle();

  if (error) throw error;
  if (!data) return null;
  return {
    content: data.has_draft ? data.draft_data : data.published_data,
    hasDraft: data.has_draft,
    updatedAt: data.updated_at,
  };
}

export async function saveCollectionDraft(slug, content, sortOrder = 0) {
  const { error } = await supabase.from("collections").upsert(
    {
      slug,
      draft_data: content,
      has_draft: true,
      sort_order: sortOrder,
      updated_at: new Date().toISOString(),
    },
    { onConflict: "slug" }
  );
  if (error) throw error;
}

export async function deleteCollection(slug) {
  const { error } = await supabase.from("collections").delete().eq("slug", slug);
  if (error) throw error;
}

/* ── Site Settings ────────────────────────────────────── */

export async function getSettingsDraft() {
  const { data, error } = await supabase
    .from("site_settings")
    .select("draft_data, published_data, has_draft, updated_at")
    .eq("id", "main")
    .maybeSingle();

  if (error) throw error;
  if (!data) return null;
  return {
    content: data.has_draft ? data.draft_data : data.published_data,
    hasDraft: data.has_draft,
    updatedAt: data.updated_at,
  };
}

export async function saveSettingsDraft(content) {
  const { error } = await supabase.from("site_settings").upsert(
    {
      id: "main",
      draft_data: content,
      has_draft: true,
      updated_at: new Date().toISOString(),
    },
    { onConflict: "id" }
  );
  if (error) throw error;
}

export const settingsSetupSQL = `-- Table des réglages (exécutez dans le SQL Editor Supabase) :

create table public.site_settings (
  id text primary key default 'main',
  draft_data jsonb,
  published_data jsonb,
  has_draft boolean default false,
  updated_at timestamptz default now()
);

alter table public.site_settings enable row level security;

create policy "Auth read" on public.site_settings
  for select to authenticated using (true);
create policy "Auth insert" on public.site_settings
  for insert to authenticated with check (true);
create policy "Auth update" on public.site_settings
  for update to authenticated using (true) with check (true);
`;

/* ── Publish System ───────────────────────────────────── */

import { collectionsList as defaultCollectionsList } from "@/data/collections";
import defaultSettings from "@/data/site-settings";

/**
 * Fetch all draft data for preview mode.
 * Returns the same structure as published.json but with draft content.
 */
export async function fetchPreviewData() {
  const snapshot = { publishedAt: null, preview: true, pages: {}, collections: {}, collectionsList: [], settings: {} };

  // Pages
  const { data: pageRows } = await supabase
    .from("page_content")
    .select("page_slug, draft_content, published_content, has_draft");

  const dbPages = {};
  if (pageRows) {
    pageRows.forEach((r) => {
      dbPages[r.page_slug] = r.has_draft ? r.draft_content : r.published_content;
    });
  }
  for (const meta of pagesMeta) {
    snapshot.pages[meta.slug] = dbPages[meta.slug] || getDefaultPage(meta.slug);
  }

  // Collections
  const { data: colRows } = await supabase
    .from("collections")
    .select("slug, draft_data, published_data, has_draft, sort_order")
    .order("sort_order", { ascending: true });

  const dbCollections = {};
  if (colRows && colRows.length > 0) {
    colRows.forEach((r) => {
      dbCollections[r.slug] = r.has_draft ? r.draft_data : r.published_data;
    });
  }

  const allSlugs = new Set([...Object.keys(dbCollections), ...Object.keys(collectionsData)]);
  for (const slug of allSlugs) {
    const col = dbCollections[slug] || getDefaultCollection(slug);
    if (col) {
      snapshot.collections[slug] = col;
      snapshot.collectionsList.push({
        name: col.name, slug: col.slug || slug, price: col.price, image: col.image, desc: col.description,
      });
    }
  }

  // Settings
  const { data: settingsRow } = await supabase
    .from("site_settings")
    .select("draft_data, published_data, has_draft")
    .eq("id", "main")
    .maybeSingle();

  if (settingsRow) {
    snapshot.settings = settingsRow.has_draft ? settingsRow.draft_data : settingsRow.published_data;
  }
  if (!snapshot.settings || Object.keys(snapshot.settings).length === 0) {
    snapshot.settings = structuredClone(defaultSettings);
  }

  return snapshot;
}

/**
 * Get a summary of all pending drafts across the CMS.
 */
export async function getPublishSummary() {
  const summary = { pages: [], collections: [], settingsHasDraft: false, lastPublishedAt: null };

  // Pages
  const { data: pageRows } = await supabase
    .from("page_content")
    .select("page_slug, has_draft, updated_at");
  if (pageRows) {
    summary.pages = pageRows
      .filter((r) => r.has_draft)
      .map((r) => {
        const meta = pagesMeta.find((p) => p.slug === r.page_slug);
        return { slug: r.page_slug, name: meta?.name || r.page_slug, updatedAt: r.updated_at };
      });
  }

  // Collections
  const { data: colRows } = await supabase
    .from("collections")
    .select("slug, draft_data, has_draft, updated_at");
  if (colRows) {
    summary.collections = colRows
      .filter((r) => r.has_draft)
      .map((r) => ({ slug: r.slug, name: r.draft_data?.name || r.slug, updatedAt: r.updated_at }));
  }

  // Settings
  const { data: settingsRow } = await supabase
    .from("site_settings")
    .select("has_draft, updated_at")
    .eq("id", "main")
    .maybeSingle();
  if (settingsRow) {
    summary.settingsHasDraft = settingsRow.has_draft;
  }

  // Last publish timestamp (check if snapshot exists)
  try {
    const { data: files } = await supabase.storage.from("snapshots").list("", { limit: 1, search: "published.json" });
    if (files && files.length > 0) {
      summary.lastPublishedAt = files[0].updated_at || files[0].created_at;
    }
  } catch {
    // bucket might not exist yet
  }

  return summary;
}

/**
 * Publish all content: generate snapshot and upload to Storage.
 */
export async function publishAll() {
  const now = new Date().toISOString();
  const snapshot = { publishedAt: now, pages: {}, collections: {}, collectionsList: [], settings: {} };

  // 1. Gather pages
  const { data: pageRows } = await supabase
    .from("page_content")
    .select("page_slug, draft_content, published_content, has_draft");
  
  const dbPages = {};
  if (pageRows) {
    pageRows.forEach((r) => {
      dbPages[r.page_slug] = r.has_draft ? r.draft_content : r.published_content;
    });
  }

  for (const meta of pagesMeta) {
    snapshot.pages[meta.slug] = dbPages[meta.slug] || getDefaultPage(meta.slug);
  }

  // 2. Gather collections
  const { data: colRows } = await supabase
    .from("collections")
    .select("slug, draft_data, published_data, has_draft, sort_order")
    .order("sort_order", { ascending: true });

  const dbCollections = {};
  if (colRows && colRows.length > 0) {
    colRows.forEach((r) => {
      dbCollections[r.slug] = r.has_draft ? r.draft_data : r.published_data;
    });
  }

  // Merge: DB collections + defaults for any missing
  const allSlugs = new Set([
    ...Object.keys(dbCollections),
    ...Object.keys(collectionsData),
  ]);
  for (const slug of allSlugs) {
    const col = dbCollections[slug] || getDefaultCollection(slug);
    if (col) {
      snapshot.collections[slug] = col;
      snapshot.collectionsList.push({
        name: col.name,
        slug: col.slug || slug,
        price: col.price,
        image: col.image,
        desc: col.description,
      });
    }
  }

  // 3. Gather settings
  const { data: settingsRow } = await supabase
    .from("site_settings")
    .select("draft_data, published_data, has_draft")
    .eq("id", "main")
    .maybeSingle();

  if (settingsRow) {
    snapshot.settings = settingsRow.has_draft ? settingsRow.draft_data : settingsRow.published_data;
  }
  if (!snapshot.settings || Object.keys(snapshot.settings).length === 0) {
    snapshot.settings = structuredClone(defaultSettings);
  }

  // 4. Upload snapshot to Storage
  const snapshotBlob = new Blob([JSON.stringify(snapshot)], { type: "application/json" });
  const { error: uploadError } = await supabase.storage
    .from("snapshots")
    .upload("published.json", snapshotBlob, {
      contentType: "application/json",
      upsert: true,
    });
  if (uploadError) throw uploadError;

  // 5. Mark all as published in DB
  if (pageRows && pageRows.length > 0) {
    for (const row of pageRows) {
      const content = row.has_draft ? row.draft_content : row.published_content;
      await supabase
        .from("page_content")
        .update({ published_content: content, has_draft: false, updated_at: now })
        .eq("page_slug", row.page_slug);
    }
  }

  if (colRows && colRows.length > 0) {
    for (const row of colRows) {
      const content = row.has_draft ? row.draft_data : row.published_data;
      await supabase
        .from("collections")
        .update({ published_data: content, has_draft: false, updated_at: now })
        .eq("slug", row.slug);
    }
  }

  if (settingsRow) {
    const content = settingsRow.has_draft ? settingsRow.draft_data : settingsRow.published_data;
    await supabase
      .from("site_settings")
      .update({ published_data: content, has_draft: false, updated_at: now })
      .eq("id", "main");
  }

  return { publishedAt: now, snapshot };
}

/**
 * Get the public URL of the published snapshot.
 */
export function getSnapshotUrl() {
  const { data } = supabase.storage.from("snapshots").getPublicUrl("published.json");
  return data?.publicUrl;
}

export const snapshotsSetupSQL = `-- Bucket snapshots (exécutez dans le SQL Editor Supabase) :

insert into storage.buckets (id, name, public)
values ('snapshots', 'snapshots', true)
on conflict (id) do nothing;

create policy "Auth upload snapshots" on storage.objects
  for insert to authenticated with check (bucket_id = 'snapshots');
create policy "Auth update snapshots" on storage.objects
  for update to authenticated using (bucket_id = 'snapshots');
create policy "Public read snapshots" on storage.objects
  for select using (bucket_id = 'snapshots');
`;


/* ── SQL Setup ────────────────────────────────────────── */

export const setupSQL = `-- Exécutez ce SQL dans le SQL Editor de votre dashboard Supabase :

create table public.page_content (
  id uuid default gen_random_uuid() primary key,
  page_slug text unique not null,
  draft_content jsonb,
  published_content jsonb,
  has_draft boolean default false,
  updated_at timestamptz default now()
);

alter table public.page_content enable row level security;

create policy "Auth read" on public.page_content
  for select to authenticated using (true);
create policy "Auth insert" on public.page_content
  for insert to authenticated with check (true);
create policy "Auth update" on public.page_content
  for update to authenticated using (true) with check (true);
`;

export const collectionsSetupSQL = `-- Table des collections (exécutez dans le SQL Editor Supabase) :

create table public.collections (
  id uuid default gen_random_uuid() primary key,
  slug text unique not null,
  draft_data jsonb,
  published_data jsonb,
  has_draft boolean default false,
  sort_order integer default 0,
  updated_at timestamptz default now()
);

alter table public.collections enable row level security;

create policy "Auth read" on public.collections
  for select to authenticated using (true);
create policy "Auth insert" on public.collections
  for insert to authenticated with check (true);
create policy "Auth update" on public.collections
  for update to authenticated using (true) with check (true);
create policy "Auth delete" on public.collections
  for delete to authenticated using (true);
`;
